// frontend/src/components/ElectronicSignatureModal.tsx
import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Checkbox,
  VStack,
  Alert,
  AlertIcon,
  useToast
} from '@chakra-ui/react'

interface ElectronicSignatureModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (signatureData: string) => void
  packageName: string
  packagePrice: number
}

const ElectronicSignatureModal = ({
  isOpen,
  onClose,
  onConfirm,
  packageName,
  packagePrice
}: ElectronicSignatureModalProps) => {
  const { t } = useTranslation()
  const toast = useToast()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)
  const [agreedPolicies, setAgreedPolicies] = useState({
    refundPolicy: false,
    serviceTerms: false,
    riskDisclosure: false
  })

  // 캔버스 초기화
  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = '#374151'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.strokeStyle = '#10B981'
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
      }
      setHasSignature(false)
    }
  }, [isOpen])

  // 모달 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setAgreedPolicies({
        refundPolicy: false,
        serviceTerms: false,
        riskDisclosure: false
      })
      setHasSignature(false)
    }
  }, [isOpen])

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    
    const rect = canvas.getBoundingClientRect()
    // 실제 표시 크기와 캔버스 내부 크기의 비율 계산
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      }
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    }
  }

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return
    
    const { x, y } = getCoordinates(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    setIsDrawing(true)
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    if (!isDrawing) return
    
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return
    
    const { x, y } = getCoordinates(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    setHasSignature(true)
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (ctx && canvas) {
      ctx.fillStyle = '#374151'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      setHasSignature(false)
    }
  }

  const handleConfirm = () => {
    if (!agreedPolicies.riskDisclosure) {
      toast({
        title: t('투자 위험 고지에 동의해주세요'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }
    
    if (!hasSignature) {
      toast({
        title: t('서명을 입력해주세요'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }
    
    const canvas = canvasRef.current
    if (canvas) {
      const signatureData = canvas.toDataURL('image/png')
      onConfirm(signatureData)
    }
  }

  const allAgreed = agreedPolicies.riskDisclosure

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="dark.card" borderColor="whiteAlpha.100" borderWidth="1px">
        <ModalHeader color="white">
          {t('전자서명 및 정책 동의')}
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* 구매 정보 */}
            <Box bg="whiteAlpha.100" p={4} borderRadius="md">
              <Text color="gray.400" fontSize="sm">{t('진행 항목')}</Text>
              <Text color="white" fontWeight="bold" fontSize="lg">{packageName}</Text>
              {packagePrice > 0 && (
                <Text color="brand.primary" fontWeight="bold" fontSize="xl">${packagePrice} USDT</Text>
              )}
            </Box>

            {/* 정책 동의 체크박스 */}
            <VStack align="stretch" spacing={2}>

              <Checkbox
                isChecked={agreedPolicies.riskDisclosure}
                onChange={(e) => setAgreedPolicies(prev => ({ ...prev, riskDisclosure: e.target.checked }))}
                colorScheme="green"
              >
                <Text color="gray.300" fontSize="sm">
                  {t('투자 위험 고지에 동의합니다. (손실 가능성 인지)')} <Text as="span" color="red.400">*</Text>
                </Text>
              </Checkbox>
            </VStack>

            {/* 서명 캔버스 */}
            <Box>
              <Text color="gray.400" fontSize="sm" mb={2}>{t('아래에 서명해주세요')}</Text>
              <Box
                border="2px dashed"
                borderColor={hasSignature ? 'brand.primary' : 'gray.600'}
                borderRadius="md"
                overflow="hidden"
              >
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  style={{ width: '100%', cursor: 'crosshair', touchAction: 'none', backgroundColor: '#374151' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </Box>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="gray"
                mt={1}
                onClick={clearCanvas}
              >
                {t('서명 지우기')}
              </Button>
            </Box>
          </VStack>
        </ModalBody>
        
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} color="gray.400">
            {t('취소')}
          </Button>
          <Button
            bg="brand.primary"
            color="white"
            _hover={{ opacity: 0.8 }}
            onClick={handleConfirm}
            isDisabled={!allAgreed || !hasSignature}
          >
            {t('동의')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ElectronicSignatureModal
